// rangeMap 표시 축의 **렌더 단일 정본**(display:'badge' 알약 + annotationKey 병기).
//
// 본문 렌더(RenderTemplate — 표 셀/계산 셀/text 노드)와 작성폼 렌더(composerWidgetTreeRender의
// readonly 값 표시)가 **같은 구현**을 import한다. 두 화면이 갈라지지 않게 하는 것이 이 모듈의
// 존재 이유다 — 배지를 새로 그리지 말고 여기서 가져다 쓸 것.
//
// 판정은 resolver의 matchRangeCriterion 단일 정본, 문자열은 applyFormat이 낸 것 그대로다.
// 이 모듈은 **감싸고 나란히 두기만** 한다(색·모양·간격).

import type { CSSProperties, ReactNode } from 'react';
import type { BindingFormat } from '@safetyhub/shared';
import { matchRangeCriterion, resolveRangeAnnotation } from './resolver';

/**
 * 구간 판정 배지(알약) — rangeMap format의 display='badge'가 그리는 단일 마크업.
 * 텍스트 자체는 applyFormat이 낸 표시 문자열 그대로이고(라벨/값+라벨), 여기서는 **감싸기만** 한다:
 * 인쇄에서 배경이 빠져도 값은 항상 남는다.
 *
 * 색은 매칭된 구간 객체의 `color`(운영자 기준표 소유 — 저작 대상 아님). hex가 아니거나 없으면
 * 색 없는 중립 배지(회색 테두리 + 상속 글자색)다 — 없는 색을 지어내지 않는다.
 * 배경/글자/테두리는 rgba 15% + hex 본색으로, 구 화면 위험도 배지(web risk-color-utils
 * hexToInlineStyle)와 같은 결을 유지한다.
 *
 * ⚠️ rc는 앱 CSS의 Tailwind JIT 스캔 밖이라 **전부 인라인 style**이다(신규 유틸 클래스 금지).
 * PDF는 screenshot 경로라 배경색이 그대로 찍히지만, 브라우저 인쇄에서도 배경이 살아남게
 * print-color-adjust:exact를 함께 명시한다.
 */
export function RangeBadge({ text, color }: { text: string; color?: string }): ReactNode {
  const hex = typeof color === 'string' && /^#[0-9a-f]{6}$/i.test(color) ? color : undefined;
  const rgba = hex
    ? `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, 0.15)`
    : undefined;
  const style: CSSProperties = {
    display: 'inline-block',
    // 알약 — 세로 여백은 줄 높이를 밀지 않게 얇게, 가로는 글자 폭 대비 넉넉히.
    padding: '1px 8px',
    borderRadius: '9999px',
    border: `1px solid ${hex ?? '#d1d5db'}`,
    backgroundColor: rgba ?? 'transparent',
    // 색 없는 구간은 글자색을 상속(중립 배지) — 임의의 색을 만들지 않는다.
    color: hex,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
  };
  return <span style={style}>{text}</span>;
}

/**
 * rangeMap 표시 노드 — 알약 배지(display='badge' + 구간 매칭)와 병기 값(annotationKey)을 **함께**
 * 그린다. 둘 다 없으면 undefined를 돌려 호출부가 종전 문자열 경로를 그대로 타게 한다(회귀 0).
 *
 * - 배지는 format이 rangeMap + display='badge'이고 **구간이 실제로 매칭될 때만**이다. 미매칭
 *   (비숫자/구간 밖/구간 미주입)이면 배지 없이 텍스트로 degrade한다(가짜 배지 금지 — applyFormat도
 *   같은 경우 값만 낸다). **degrade해도 병기는 남는다** — 근거는 배지의 부속물이 아니다.
 * - 병기는 표시 **밖**에 나란히 둔다(구간 색은 배지만 소유 — 병기에 색을 입히지 않는다).
 *   간격은 인라인 style(rc는 앱 CSS의 Tailwind JIT 스캔 밖이라 유틸 클래스를 쓸 수 없다).
 *   배지 옆이든 텍스트 옆이든 **한 줄에 나란히**이고, 값이 비면 아무것도(빈 span·여분 공백조차)
 *   그리지 않는다(resolveRangeAnnotation doc).
 *
 * raw는 **format 적용 전 원값**이어야 한다(구간 판정 입력). text는 applyFormat 결과 문자열.
 * scope는 동적 기준표(rangesFrom 카탈로그 키 / rangesKey 경로)와 병기 값(annotationKey) 조회
 * 스코프 — 본문은 RenderData flat, 작성폼은 composerWidgetTreeRender가 합성한 표시
 * 스코프(그쪽 writeFormatScope doc 참조).
 */
export function rangeDisplayNode(
  raw: unknown,
  format: BindingFormat | undefined,
  scope: unknown,
  text: string,
): ReactNode | undefined {
  if (format?.kind !== 'rangeMap') return undefined;
  const annotation = resolveRangeAnnotation(format, scope);
  // 빈 표시 문자열에는 배지를 씌우지 않는다(빈 알약 금지) — 종전 게이트 그대로.
  const matched =
    format.display === 'badge' && text !== '' ? matchRangeCriterion(raw, format, scope) : undefined;
  if (!matched) {
    // 배지 없음 + 병기 없음 = 이 모듈이 관여할 게 없다(호출부의 문자열 경로 유지).
    // 표시 본값이 비어도 마찬가지다 — 병기는 값의 부속이지 값을 대신하지 못한다(`— 근거`처럼
    // 근거만 덩그러니 남는 칸 금지). 빈 칸은 호출부의 종전 빈값 경로가 그대로 처리한다.
    if (annotation === '' || text === '') return undefined;
    // display='text'(및 배지 degrade) — 표시 문자열 뒤에 공백 하나 두고 병기. 문자열 하나로
    // 돌려주므로 호출부의 문자열 처리(순번 prefix·`(계속)` 접미어 연결)가 그대로 산다.
    return `${text} ${annotation}`;
  }
  const badge = <RangeBadge text={text} color={matched.color} />;
  if (annotation === '') return badge;
  return (
    <>
      {badge}
      {/* 알약 바깥의 병기 — inline 흐름이라 칸이 좁으면 배지 다음 줄로 자연히 넘어가고,
          글자 기준선도 배지와 그대로 맞는다(flex 컨테이너로 감싸면 둘 다 잃는다). */}
      {/* 좁은 칸(모바일)에서 줄바꿈이 어절을 쪼개지 않게 — '중대성'이 '중 / 대성'으로 갈리면
          읽는 사람이 한 번 멈춘다. 데스크톱은 한 줄이라 이 속성이 작동할 일이 없다(뷰별 분기를
          두지 않는 이유 — rc는 인라인 style이라 미디어쿼리를 못 쓰고, JS 뷰포트 감지는 얻는 것
          없이 복잡해진다). 어절 하나가 칸보다 길면 overflowWrap이 그때만 강제로 끊는다. */}
      <span
        style={{ marginLeft: '6px', wordBreak: 'keep-all', overflowWrap: 'break-word' }}
      >
        {annotation}
      </span>
    </>
  );
}
